/**
 * Trading preferences.
 *
 * **This screen exists because AEGIS must not have a house trading style.**
 * The founder trades futures, and every setting here is a choice that would
 * otherwise be a constant encoding how *he* works: which timezone a trading day
 * is measured in, whether P&L matches FIFO or LIFO, whether a session means RTH
 * or ETH, whether a daily loss counts open positions, when the front month
 * rolls.
 *
 * Before this screen those values had defaults and no way to change them, which
 * is a house style wearing a default's clothes. A preference nobody can set is a
 * decision made on the user's behalf.
 *
 * So the copy does two things everywhere: it says what the choice *is* in the
 * user's own vocabulary, and it says what changes if they pick differently.
 * Neither "FIFO" nor "LIFO" means anything without the second half.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { api, ApiError } from '../lib/api.js';
import type { Preferences, PreferencesResponse } from '../lib/api.js';
import { Link } from '../lib/router.js';

/** A handful of zones plus every one the browser knows, which covers the rest. */
function zoneOptions(): string[] {
  const common = [
    'exchange',
    'America/Chicago', 'America/New_York', 'America/Los_Angeles',
    'Europe/London', 'Europe/Zurich', 'Asia/Tokyo', 'Asia/Singapore',
    'Australia/Sydney', 'UTC',
  ];
  try {
    const all = (Intl as unknown as { supportedValuesOf?(k: string): string[] })
      .supportedValuesOf?.('timeZone') ?? [];
    return [...new Set([...common, ...all])];
  } catch {
    return common;
  }
}

export function PreferencesScreen(): JSX.Element {
  const [prefs, setPrefs] = useState<PreferencesResponse | null>(null);
  const [draft, setDraft] = useState<Preferences | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const load = useCallback(async () => {
    try {
      const p = await api.preferences();
      setPrefs(p);
      setDraft({
        timezone: p.timezone, matchingMethod: p.matchingMethod,
        sessionScope: p.sessionScope, dailyLossBasis: p.dailyLossBasis,
        rollRule: p.rollRule, breakevenBandUsd: p.breakevenBandUsd,
        defaultTimeframe: p.defaultTimeframe, chartStyle: p.chartStyle,
        /*
          Carried through untouched. The widget picker lives on the phone —
          it is a setting about a surface the desktop does not have — but this
          screen PUTs the whole preferences object, so dropping the fields
          here would silently reset a trader's widget every time they saved a
          timezone.
        */
        widgetConcept: p.widgetConcept, widgetAccountId: p.widgetAccountId,
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load preferences.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!draft || pending) return;
    setPending(true); setError(null); setSaved(false);
    try {
      setPrefs(await api.savePreferences(draft));
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    } finally {
      setPending(false);
    }
  }

  if (!draft || !prefs) {
    return <div className="scroll-area"><h2>Trading preferences</h2><p className="dim">{error ?? 'Loading…'}</p></div>;
  }

  const set = <K extends keyof Preferences>(k: K, v: Preferences[K]): void => {
    setDraft({ ...draft, [k]: v });
    setSaved(false);
  };

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>Trading preferences</h2>
        <Link to="/settings" className="btn btn-quiet compact">Back to settings</Link>
      </div>

      <p className="panel-body">
        AEGIS has no opinion about how you trade. These decide how your own numbers
        are computed, and every one of them changes what the journal reports.
      </p>
      {!prefs.customised && (
        <p className="field-hint">
          You have not changed any of these, so the neutral defaults are in force.
        </p>
      )}

      {error && <div className="form-error" role="alert">{error}</div>}
      {saved && <div className="form-ok" role="status">Saved. Analytics recompute on the next load.</div>}

      <form onSubmit={onSubmit} className="settings-form pref-form" noValidate>
        <label className="field">
          <span className="field-label">Trading day timezone</span>
          <select
            className="input select-wide" value={draft.timezone}
            onChange={(e) => set('timezone', e.target.value)}
          >
            {zoneOptions().map((z) => (
              <option key={z} value={z}>{z === 'exchange' ? "Follow each contract's exchange" : z}</option>
            ))}
          </select>
          <span className="field-hint">
            Decides where one trading day ends and the next begins, which decides
            which day a trade counts toward and when a daily loss limit resets.
            Following the exchange is neutral; pick a zone if you think in yours.
          </span>
        </label>

        {/*
          Where the chart opens, and how it is drawn.

          Synced rather than device-local, unlike the last symbol viewed: this
          is a considered choice about how the user reads a market, not where
          they happened to leave the chart. Set here, or by the first-run
          questionnaire — both write the same two fields.
        */}
        <label className="field">
          <span className="field-label">Chart opens on</span>
          <select
            className="input select-wide" value={draft.defaultTimeframe}
            onChange={(e) => set('defaultTimeframe', e.target.value as Preferences['defaultTimeframe'])}
          >
            <option value="1m">1 minute</option>
            <option value="5m">5 minutes</option>
            <option value="15m">15 minutes</option>
            <option value="1h">1 hour</option>
            <option value="4h">4 hours</option>
            <option value="1d">1 day</option>
          </select>
          <span className="field-hint">
            The timeframe a chart opens on, on every device. Changing the chart
            while you work does not change this.
          </span>
        </label>

        <label className="field">
          <span className="field-label">Chart drawn as</span>
          <select
            className="input select-wide" value={draft.chartStyle}
            onChange={(e) => set('chartStyle', e.target.value as Preferences['chartStyle'])}
          >
            <option value="candles">Candles</option>
            <option value="hollow">Hollow candles</option>
            <option value="bars">Bars</option>
            <option value="line">Line</option>
          </select>
          <span className="field-hint">
            Presentation only — it changes how a bar looks, never what any figure
            says.
          </span>
        </label>

        <label className="field">
          <span className="field-label">Lot matching</span>
          <select
            className="input select-wide" value={draft.matchingMethod}
            onChange={(e) => set('matchingMethod', e.target.value as Preferences['matchingMethod'])}
          >
            <option value="fifo">FIFO — oldest lot closes first</option>
            <option value="lifo">LIFO — newest lot closes first</option>
            <option value="average">Average cost</option>
          </select>
          <span className="field-hint">
            Which open lot a closing fill is matched against when you scale in and
            out. It changes per-trade P&amp;L and holding times. Set it to whatever
            your broker's statement uses — a mismatch shows up as a journal that
            disagrees with the statement by a few dollars a trade, which is the
            fastest way to stop trusting it.
          </span>
        </label>

        <label className="field">
          <span className="field-label">Session scope</span>
          <select
            className="input select-wide" value={draft.sessionScope}
            onChange={(e) => set('sessionScope', e.target.value as Preferences['sessionScope'])}
          >
            <option value="eth">Whole electronic session (ETH)</option>
            <option value="rth">Regular session only (RTH)</option>
          </select>
          <span className="field-hint">
            Which hours analytics cover. RTH is the pit-equivalent day; ETH is
            everything. An overnight trader on RTH would see their own trades
            excluded and conclude the product was broken, so the default is ETH.
          </span>
        </label>

        <label className="field">
          <span className="field-label">Daily loss counts</span>
          <select
            className="input select-wide" value={draft.dailyLossBasis}
            onChange={(e) => set('dailyLossBasis', e.target.value as Preferences['dailyLossBasis'])}
          >
            <option value="realized">Closed trades only</option>
            <option value="realized_plus_unrealized">Closed trades plus open positions</option>
          </select>
          <span className="field-hint">
            <strong>Match this to your prop firm's rule, not your preference.</strong>{' '}
            Firms differ, and the difference decides whether you are in breach while
            a losing position is still open. Getting it wrong here means the gauge
            says you are fine when your firm says you are out.
          </span>
        </label>

        <label className="field">
          <span className="field-label">Contract roll</span>
          <select
            className="input select-wide" value={draft.rollRule}
            onChange={(e) => set('rollRule', e.target.value as Preferences['rollRule'])}
          >
            <option value="volume">When volume moves to the next month</option>
            <option value="open_interest">When open interest moves</option>
            <option value="calendar">A fixed number of days before expiry</option>
          </select>
          <span className="field-hint">
            When the chart and the journal switch to the next contract month.
          </span>
        </label>

        <label className="field">
          <span className="field-label">Breakeven band</span>
          <div className="field-prefixed">
            <span className="field-prefix">$</span>
            <input
              className="input" type="text" inputMode="decimal"
              value={draft.breakevenBandUsd}
              onChange={(e) => set('breakevenBandUsd', e.target.value)}
              aria-describedby="be-hint"
            />
          </div>
          <span className="field-hint" id="be-hint">
            How far either side of zero still counts as flat. A trade that closes
            for two dollars after commissions is arithmetically a win and
            practically a scratch, and counting it as a win inflates your win rate
            in the flattering direction.{' '}
            <strong>This changes what "win" means everywhere</strong> — analytics,
            every report, the score — and never changes your P&amp;L. The right
            number depends on what you trade: a few dollars is noise on a ten-lot
            and a real result on a single micro. Zero is strict arithmetic, which
            is what the tax export uses regardless.
          </span>
        </label>

        <button className="btn btn-primary" type="submit" disabled={pending}>
          {pending ? 'Saving…' : 'Save preferences'}
        </button>
      </form>
    </div>
  );
}
